import React, { useRef, useState, useEffect } from 'react';
import { message } from 'antd';
import { showErrorMessage } from 'lowcomp/plugins/member/utils';
import { getLang } from 'lowcomp/plugins/member/utils';
import css from './index.module.scss';
import NextImage from '@/components/NextImage';
import { checkRewardDraw, rewardDraw } from '@/service';
import getLocal from '@/app/utils/getLocal';
import EditorImg from 'lowcode/components/EditorImg';
import { textLang } from 'lowcomp/plugins/member/common/textLang';
import { useRouter } from 'next/router';
import gtm from '@/utils/gtm';
import { signupCookie } from '@/utils/cookies/signupCookie';
import { createPortal } from 'react-dom';
import LoginFormModal from '@/components/LoginFormModal';

interface Prizes {
    drawId: React.Key; // 抽奖活动ID
    id: React.Key;
    isDisplayName: boolean;
    prizeId: React.Key; // 奖品ID
    prizeName: string;
    tips: string;
    type: 'NOTHING' | 'INTEGRAL' | 'COUPONS' | 'FREE_PRODUCTS' | 'THIRD_COUPONS';
    iconUrl?: string;
    isDisplayIcon?: boolean;
}

interface LuckyWheelProps {
    isLogin?: boolean;
    prizes: Prizes[];
    duration?: number;
    circle?: number;
    disabled?: boolean;
    drawInfo?: any;
    onStartRotating?: () => void;
    onEndRotating?: (winnerPrize?: any) => void;
    onCheckError?: () => void;
    pathUrl?: string;
    bgImage: string;
    btnImage: string;
    btnDisabledImage: string;
    loginType?: 'page' | 'modal';
}

const CIRCLE_ANGLE = 360;

const LuckyWheel: React.FC<LuckyWheelProps> = ({
    isLogin,
    prizes,
    duration = 10 * 1000,
    circle = 12,
    disabled = false,
    drawInfo,
    onStartRotating,
    onEndRotating,
    onCheckError,
    pathUrl,
    bgImage,
    btnImage,
    btnDisabledImage,
    loginType = 'page',
}) => {
    const router = useRouter();
    const { locale, pathname } = getLocal();
    const t = getLang(textLang, locale);
    const [isRotating, setIsRotaing] = useState(false);
    const rotateAngle = useRef(0);
    const ref1 = useRef<HTMLDivElement>(null);

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isClient, setIsClient] = useState(false);
    /**
     * @desc 每个奖增加style
     */
    const formatPrizeList = (prizes: Prizes[]) => {
        const l = prizes.length;
        const average = CIRCLE_ANGLE / l; // 计算单个奖项所占的角度
        const half = average / 2;
        const rightBig = l === 2 ? 50 : 0;
        const heightBig = l <= 3 ? '100' : '50';
        const topBig = l === 3 ? '-50' : '0';
        const skewMain = l <= 2 ? 0 : 360 / l - 90;
        // 循环计算给每个奖项添加style属性

        return prizes?.map((prize, i) => {
            // 每个奖项旋转的位置为 当前 i * 平均值 + 平均值 / 2
            const angle = -(i * average + half);
            const bigAge = l > 2 ? (i * CIRCLE_ANGLE) / l : '0';

            return {
                ...prize,
                whellStyle: {
                    transform: `rotate(${bigAge}deg) skewY(${skewMain}deg)`,
                    WebkitTransform: `rotate(${bigAge}deg) skewY(${skewMain}deg)`,
                    right: `${rightBig * i}%`,
                    height: `${heightBig}%`,
                    top: `${topBig}%`,
                    width: `${l == 1 ? 100 : 50}%`,
                },
                prizeStyle: {
                    transform: `rotate(${-angle}deg)`,
                    WebkitTransform: `rotate(${-angle}deg)`,
                },
            };
        });
    };

    /**
     * 根据奖项中奖Id找到转动的角度
     */
    const getAngle = (prizeList: Prizes[], awardsId?: string) => {
        const prizeIndex = prizeList?.findIndex((prize) => prize.id === awardsId);
        const noRewardIndex = prizeList?.findIndex((prize) => prize.type === 'NOTHING');

        if (prizeIndex !== -1 || noRewardIndex !== -1) {
            const index = prizeIndex === -1 ? noRewardIndex : prizeIndex;
            const average = CIRCLE_ANGLE / prizeList?.length;
            const half = average / 2;
            const prizeAngle = CIRCLE_ANGLE - (average * index + half);
            // 当前角度 + 转到0度需要转的度数 + 固定圈数 + 转到奖品需要的度数
            const angle = rotateAngle.current - (rotateAngle.current % CIRCLE_ANGLE) + circle * CIRCLE_ANGLE + prizeAngle;
            rotateAngle.current = angle;
            return angle;
        } else {
            // 转到原点
            const angle = rotateAngle.current + circle * CIRCLE_ANGLE + (CIRCLE_ANGLE - (rotateAngle.current % CIRCLE_ANGLE));
            rotateAngle.current = angle;
            return angle;
        }
    };

    /**
     * @desc 转盘转动角度
     */
    const onRotating = async () => {
        if (disabled) return;
        if (isRotating) return;
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_button',
            page_name: pathname,
            list_name: 'Lucky Spin',
            button_name: 'Go',
            function: 'Lucky Spin',
        });
        if (!isLogin) {
            signupCookie({
                sector_name: 'lucky-whell',
                entry_position: 0,
            });
            if (loginType === 'modal') {
                setShowLoginModal(true);
                return;
            }
            router.push(`/${locale}/login?redirect=${window.location.href}`);
            return;
        }

        setIsRotaing(true);

        // check
        const { id: drawId, version } = drawInfo;
        try {
            const checkRes = await checkRewardDraw({ drawId, version }, locale, pathUrl);
            if (checkRes?.code !== '0') {
                setIsRotaing(false);
                showErrorMessage(checkRes?.code, t);
                onCheckError?.();
                return;
            }
        } catch (e) {
            message.error(t('network_error'));
            setIsRotaing(false);
            onCheckError?.();
            console.error('e', e);
            return;
        }

        // 先转起来
        onStartRotating?.();
        const defaultAngle = rotateAngle.current + circle * CIRCLE_ANGLE;
        ref1.current && (ref1.current.style.transform = `rotate(${defaultAngle}deg)`);

        // 获取奖项id
        const prizeRes = await rewardDraw({ drawId, version }, locale, pathUrl);
        if (prizeRes?.code === '0') {
            const { awardsId } = prizeRes?.data || {};
            const angle = getAngle(prizeList, awardsId);
            ref1.current && (ref1.current.style.transform = `rotate(${angle}deg)`);

            // 旋转结束 弹框
            setTimeout(() => {
                setIsRotaing(false);
                onEndRotating?.(prizeRes?.data || {});
            }, duration + 100);
        } else {
            // 找到未中奖的奖项
            const angle = getAngle(prizeList);
            ref1.current && (ref1.current.style.transform = `rotate(${angle}deg)`);
            showErrorMessage(prizeRes?.code, t);
            setTimeout(() => {
                setIsRotaing(false);
                onEndRotating?.();
            }, duration + 100);
        }
    };

    const prizeList = formatPrizeList(prizes);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    }, []);

    return (
        <div className={css['luckBg']}>
            {/* 旋转内容 */}
            <div
                ref={ref1}
                className="luck-whell-bg"
                style={{
                    transition: `transform ${duration}ms cubic-bezier(0.14, 0.7, 0, 1)`,
                    WebkitTransition: `transform ${duration}ms cubic-bezier(0.14, 0.7, 0, 1)`,
                }}
            >
                <div className="luck-border">
                    <EditorImg pc={bgImage} />
                    {/* <NextImage
                        webpSrc={require('@publicLocal/ecocredits/lucky-border.webp')}
                        src={require('@publicLocal/ecocredits/lucky-border.png')}
                        alt=""
                    /> */}
                </div>
                <div className={`luck-whell-bg-main ${prizeList?.length % 2 === 1 ? 'odd' : 'even'}`}>
                    {prizeList?.map((prize) => (
                        <div key={prize?.id} className="luck-whell-sector" style={prize?.whellStyle}></div>
                    ))}
                </div>
                <div className="wheel-main">
                    <div className="prize-list">
                        {prizeList?.map((prize) => (
                            <div key={prize.id} className="prize-item" style={prize.prizeStyle}>
                                {!!prize.isDisplayName && !!prize.prizeName && <div className="prize-name">{prize?.prizeName}</div>}

                                {!!prize.isDisplayIcon && !!prize.iconUrl && (
                                    <div className="prize-icon" style={{ backgroundImage: `url("${prize.iconUrl}")` }}>
                                        {/* <NextImage alt="" src={prize.iconUrl} /> */}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="prize_point test" onClick={onRotating}>
                {disabled ? (
                    <EditorImg pc={btnDisabledImage} />
                ) : (
                    // <NextImage
                    //     alt=""
                    //     webpSrc={require('@publicLocal/ecocredits/lucky-btn-disabled..webp')}
                    //     src={require('@publicLocal/ecocredits/lucky-btn-disabled.png')}
                    // />
                    <EditorImg pc={btnImage} />
                    // <NextImage alt="" webpSrc={require('@publicLocal/ecocredits/lucky-btn.webp')} src={require('@publicLocal/ecocredits/lucky-btn.png')} />
                )}
            </div>
            {isClient &&
                showLoginModal &&
                createPortal(
                    <LoginFormModal
                        visible={showLoginModal}
                        onCancel={() => {
                            setShowLoginModal(false);
                        }}
                        type="passwordless"
                    />,
                    document.body,
                )}
        </div>
    );
};

export default LuckyWheel;
